import * as React from "react";

import { Plus } from "@/shared/foundation/icons";
import { Popover, Tag } from "@/shared/foundation/primitives";

import styles from "./EntityHeader.module.css";

export interface EntityHeaderTagsProps {
  /** The record's tags, in the order shown. */
  value: string[];
  /** Offers to add a tag. Tags already on the record can't be removed here. */
  canAdd: boolean;
  /** Saves one new tag, typed into the "+ Tag" popover. */
  onAdd: (tag: string) => void;
  /** What clicking a tag does, as searching for records with it. */
  onClick?: (tag: string) => void;
}

/**
 * The tags, as the app's Tag pills, read-only. For someone who can edit the
 * record, a dashed "+ Tag" pill at the end.
 */
export function HeaderTags({
  value,
  canAdd,
  onAdd,
  onClick,
}: EntityHeaderTagsProps) {
  return (
    <div className={styles.tags} data-testid="entity-header-tags">
      {value.map((tag) => (
        <Tag key={tag} label={tag} onClick={onClick && (() => onClick(tag))} />
      ))}
      {canAdd && (
        <AddTagPopover
          onAdd={onAdd}
          trigger={
            <button type="button" className={styles.addTagChip}>
              <Plus aria-hidden className={styles.plusIcon} />
              Tag
            </button>
          }
        />
      )}
    </div>
  );
}

/** A small field that adds one tag on Enter, opened from `trigger`. */
export function AddTagPopover({
  onAdd,
  trigger,
}: {
  onAdd: (tag: string) => void;
  trigger: React.ReactElement;
}) {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState("");

  const commit = () => {
    const tag = draft.trim();
    if (tag) {
      onAdd(tag);
    }
    setDraft("");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Popover.Trigger render={trigger} />
      <Popover.Content aria-label="Add a tag" className={styles.addTagPopover}>
        <input
          className={styles.addTagInput}
          aria-label="New tag"
          placeholder="New tag"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              commit();
            }
          }}
        />
      </Popover.Content>
    </Popover>
  );
}
